import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import LoadingState from "@/components/base/LoadingState";
import StatusBadge from "@/components/base/StatusBadge";
import PaymentStatusBadge from "@/components/base/PaymentStatusBadge";
import OverviewTab from "./tabs/OverviewTab";
import TextTab from "./tabs/TextTab";
import PaymentsTab from "./tabs/PaymentsTab";
import SignatureTab from "./tabs/SignatureTab";
import FilesTab from "./tabs/FilesTab";
import AddOnsTab from "./tabs/AddOnsTab";
import ActivityTab from "./tabs/ActivityTab";

type Contract = {
  id: string;
  tenant_id: string;
  client_id: string;
  contract_number: string;
  title: string;
  event_date: string;
  event_location: string;
  package_name: string;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  status: string;
  signed_at: string | null;
  client_name: string;
  template_id: string | null;
  rendered_content: string | null;
  signature_status: string | null;
  client_signature: string | null;
  signature_ip: string | null;
  payment_link: string | null;
  payment_status: string;
  created_at: string;
  updated_at: string;
};

type Tab = "overview" | "text" | "payments" | "signature" | "files" | "addons" | "activity";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "overview",   label: "Overview",       icon: "ri-layout-grid-line" },
  { id: "text",       label: "Contract Text",  icon: "ri-file-text-line" },
  { id: "payments",   label: "Payments",       icon: "ri-money-dollar-circle-line" },
  { id: "signature",  label: "Signature",      icon: "ri-pen-nib-line" },
  { id: "addons",     label: "Add-ons",         icon: "ri-puzzle-line" },
  { id: "files",      label: "Files & Print",  icon: "ri-printer-line" },
  { id: "activity",   label: "Activity",        icon: "ri-history-line" },
];

export default function ContractDetailPage() {
  const navigate = useNavigate();
  const { contractId } = useParams<{ contractId: string }>();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState("");
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [notFound, setNotFound] = useState(false);

  const fetchContract = useCallback(async (tid: string, cid: string) => {
    setLoading(true);
    const [contractRes, tenantRes] = await Promise.all([
      supabase.from("contracts").select("*").eq("id", cid).eq("tenant_id", tid).maybeSingle(),
      supabase.from("saas_tenants").select("name").eq("id", tid).maybeSingle(),
    ]);
    if (contractRes.error || !contractRes.data) {
      setNotFound(true);
    } else {
      setContract(contractRes.data as Contract);
    }
    if (!tenantRes.error && tenantRes.data) {
      setTenantName((tenantRes.data as { name: string }).name ?? "");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid && contractId) fetchContract(tid, contractId);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchContract, contractId]);

  const handleContractUpdate = (updated: Partial<Contract>) => {
    setContract((prev) => prev ? { ...prev, ...updated } : prev);
  };

  const handlePaymentStatusChange = (status: string, balance: number) => {
    setContract((prev) => prev ? { ...prev, payment_status: status, balance_amount: balance } : prev);
  };

  if (loading) {
    return (
      <AdminLayout>
        <LoadingState />
      </AdminLayout>
    );
  }

  if (notFound || !contract) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5">
            <i className="ri-file-text-line text-slate-300 text-3xl"></i>
          </div>
          <div className="text-center">
            <p className="text-[#0f172a] font-semibold text-lg mb-1">Contract not found</p>
            <p className="text-slate-400 text-sm">This contract doesn&apos;t exist or you don&apos;t have access.</p>
          </div>
          <button
            onClick={() => navigate("/admin/contracts")}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0f172a] border border-white/10 px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Contracts
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page header */}
      <div className="px-4 md:px-8 pt-6 pb-0">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
          <button
            onClick={() => navigate("/admin/contracts")}
            className="hover:text-slate-500 transition-colors cursor-pointer"
          >
            Contracts
          </button>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-slate-500">{contract.contract_number}</span>
        </div>

        {/* Title row */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-start gap-4">
            <button
              onClick={() => navigate("/admin/contracts")}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-[#0f172a] transition-all cursor-pointer flex-shrink-0 mt-0.5"
            >
              <i className="ri-arrow-left-line text-sm"></i>
            </button>
            <div>
              <h1 className="text-[#0f172a] font-bold text-xl leading-tight">{contract.title}</h1>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                <span className="text-slate-400 text-sm font-mono">{contract.contract_number}</span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-400 text-sm">{contract.client_name}</span>
                {contract.event_date && (
                  <>
                    <span className="text-slate-300">·</span>
                    <span className="text-slate-400 text-sm">{contract.event_date}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusBadge status={contract.status} />
            <PaymentStatusBadge status={contract.payment_status} />
            {contract.signature_status === "signed" && (
              <span className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-400/10 px-2.5 py-1 rounded-full">
                <i className="ri-checkbox-circle-fill"></i>
                Signed
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-slate-200">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                activeTab === tab.id
                  ? "text-[#C8A96E] border-[#C8A96E]"
                  : "text-slate-400 border-transparent hover:text-slate-600"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={tab.icon}></i>
              </span>
              {tab.label}
              {/* Badge for signature tab */}
              {tab.id === "signature" && contract.signature_status === "signed" && (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              )}
              {/* Badge for payments tab */}
              {tab.id === "payments" && Number(contract.balance_amount) > 0 && (
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0"></span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="px-4 md:px-8 py-6">
        {activeTab === "overview" && tenantId && (
          <OverviewTab contract={contract} tenantId={tenantId} />
        )}
        {activeTab === "text" && tenantId && (
          <TextTab
            contract={contract}
            tenantId={tenantId}
            onUpdated={handleContractUpdate}
          />
        )}
        {activeTab === "payments" && tenantId && (
          <PaymentsTab
            contract={contract}
            tenantId={tenantId}
            onPaymentStatusChange={handlePaymentStatusChange}
          />
        )}
        {activeTab === "signature" && tenantId && (
          <SignatureTab
            contract={contract}
            tenantId={tenantId}
            onUpdated={handleContractUpdate}
          />
        )}
        {activeTab === "addons" && tenantId && (
          <AddOnsTab
            contract={{
              id:             contract.id,
              client_id:      contract.client_id,
              client_name:    contract.client_name,
              tenant_id:      tenantId,
              event_date:     contract.event_date,
              event_location: contract.event_location,
              title:          contract.title,
            }}
            tenantId={tenantId}
          />
        )}
        {activeTab === "files" && (
          <FilesTab contract={contract} tenantName={tenantName} />
        )}
        {activeTab === "activity" && tenantId && (
          <ActivityTab
            contractId={contract.id}
            clientId={contract.client_id}
            tenantId={tenantId}
          />
        )}
      </div>
    </AdminLayout>
  );
}
